"use client";


import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { ArrowRight } from "lucide-react";

// Détection in-app browser (Threads, Instagram, Facebook...)
function isInAppBrowser() {
    try {
        if (typeof window === "undefined") return false;
        const ua = window.navigator.userAgent || "";
        return /Instagram|FBAN|FBAV|Messenger|Threads/i.test(ua);
    } catch {
        return false;
    }
}

export default function LoginPage() {
    const router = useRouter();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [inAppBrowser, setInAppBrowser] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        let nextMessage = "";

        if (params.get("error") === "oauth") {
            const reason = params.get("reason");

            if (reason === "missing_code") {
                nextMessage = "Connexion Google interrompue. Reessaie.";
            } else if (reason === "exchange_failed") {
                nextMessage = "La session Google n'a pas pu etre finalisee. Reessaie.";
            } else {
                nextMessage = "La connexion Google a echoue. Reessaie.";
            }
        }

        // Ajout détection in-app browser (Threads, Instagram...)
        const detectedInAppBrowser = isInAppBrowser();
        if (detectedInAppBrowser) {
            nextMessage = "Tu arrives depuis Threads ? Ouvre ce lien dans Safari ou Chrome 🙂";
        }

        const timeout = window.setTimeout(() => {
            setInAppBrowser(detectedInAppBrowser);
            if (nextMessage) {
                setMessage(nextMessage);
            }
        }, 0);

        return () => window.clearTimeout(timeout);
    }, []);

    function toFrenchAuthError(raw: string): string {
        const normalized = raw.toLowerCase();

        if (normalized.includes("email not confirmed") || normalized.includes("email not verified")) {
            return "Ton email n'est pas encore confirmé. Vérifie ta boîte mail et les indésirables.";
        }

        if (normalized.includes("invalid login credentials")) {
            return "Email et/ou mot de passe erroné.";
        }

        return "Impossible de se connecter pour le moment.";
    }

    function getOAuthCallbackOrigin(): string {
        const configuredOrigin = process.env.NEXT_PUBLIC_OAUTH_REDIRECT_ORIGIN?.trim();

        if (configuredOrigin) {
            return configuredOrigin.replace(/\/$/, "");
        }

        return window.location.origin;
    }

    async function handleGoogleLogin() {
        setGoogleLoading(true);
        setMessage("");

        const params = new URLSearchParams(window.location.search);
        const rawNext = params.get("next") || "/dashboard";
        const nextPath = rawNext.startsWith("/") ? rawNext : "/dashboard";
        const callbackOrigin = getOAuthCallbackOrigin();
        const redirectTo = `${callbackOrigin}/auth/callback?next=${encodeURIComponent(nextPath)}`;
        const { error } = await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
                redirectTo,
                queryParams: {
                    access_type: "offline",
                    prompt: "select_account",
                },
            },
        });

        if (error) {
            console.error("Google login error:", error.message);
            setMessage("Connexion Google indisponible pour le moment.");
            setGoogleLoading(false);
        }
    }

    async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        setLoading(true);
        setMessage("");

        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            setMessage(toFrenchAuthError(error.message));
            setLoading(false);
            return;
        }
        
        const params = new URLSearchParams(window.location.search);
        const rawNext = params.get("next") || "/dashboard";
        const nextPath = rawNext.startsWith("/") ? rawNext : "/dashboard";

        router.push(nextPath);
        router.refresh();
    }

    return (
        <main className="min-h-screen flex flex-col items-center justify-center px-4 pt-12 bg-linear-to-br from-violet-50 via-white to-violet-50/50">
            <div className="w-full max-w-sm">
                <div className="mb-8 text-center">
                    <div className="mb-4 flex justify-center">
                        <img 
                            src="/icons/icon-192.png" 
                            alt="Noread logo" 
                            className="h-16 w-16 rounded-3xl shadow-lg"
                        />
                    </div>
                    <h1 className="text-5xl font-black text-slate-950 mb-2 tracking-tight">NOREAD</h1>
                    <p className="text-slate-600 font-medium">Ton app de suivi de lecture</p>
                    <p className="text-sm text-slate-500 mt-2">
                        Simple, rapide, efficace.
                    </p>
                </div>

                <form onSubmit={handleLogin} className="w-full flex flex-col gap-4">
                    <h2 className="text-xl font-bold text-center text-slate-900">Connexion</h2>

                <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading || googleLoading}
                    className="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <span className="text-base" aria-hidden="true">G</span>
                    {googleLoading ? "Redirection vers Google..." : "Continuer avec Google"}
                </button>

                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span>ou</span>
                    <span className="h-px flex-1 bg-slate-200" />
                </div>

                <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border rounded-xl px-4 py-3"
                />

                <div className="relative">
                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Mot de passe"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full border rounded-xl px-4 py-3 pr-24"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className="absolute inset-y-0 right-3 my-auto h-fit text-xs font-medium text-slate-600 hover:text-slate-800"
                    >
                        {showPassword ? "Masquer" : "Afficher"}
                    </button>
                </div>

                <div className="-mt-1 text-right">
                    <Link href="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-700">
                        Mot de passe oublié ?
                    </Link>
                </div>

                <button
                    type="submit"
                    disabled={loading || googleLoading}
                    className="bg-indigo-600 text-white rounded-xl py-3"
                >
                    {loading ? "Connexion..." : "Se connecter"}
                </button>


                                {message && (
                                        <p className="text-sm text-center text-slate-600">{message}</p>
                                )}

                                {inAppBrowser && (
                                    <a
                                        href={typeof window !== "undefined" ? window.location.href : "/login"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white py-3 text-sm font-medium mt-2"
                                    >
                                        Ouvrir dans le navigateur <ArrowRight className="h-4 w-4" strokeWidth={2} />
                                    </a>
                                )}

                    <p className="text-sm text-center">
                        Pas encore de compte ?{" "}
                        <Link href="/signup" className="text-indigo-600">
                            S&apos;inscrire
                        </Link>
                    </p>

                    <p className="text-xs text-center text-slate-500 leading-5">
                        En continuant, tu acceptes nos{" "}
                        <Link href="/terms" className="text-indigo-600 hover:text-indigo-700">
                            Conditions d&apos;utilisation
                        </Link>
                        {" "}et notre{" "}
                        <Link href="/privacy" className="text-indigo-600 hover:text-indigo-700">
                            Politique de confidentialité
                        </Link>
                        .
                    </p>
                </form>
            </div>
        </main>
    );
}
